import * as React from "react";
declare const I18n;

import { Course, MediaFile } from "../../../../types";

import ListItem from "../../components/list_item";
import Header from "../../../Header";
import AnnouncementBar from "../../../AnnouncementBar/AnnouncementBar";

import api from "../../../../api";

type Category = {
  id: number
  name: string
  name_fr?: string
  description?: string
  description_fr?: string
  cover_image_url?: string
  icon_class?: string
  slug?: string
}

type Props = {
  initialData: {
    courses: Course[];
    categories: Category[];
    image: MediaFile;
    userType?: string;
    userLevel?: number;
    notice?: string;
  };
};

const IndexCourse: React.FunctionComponent<Props> = ({
  initialData: { courses, categories, image, userType, userLevel, notice },
}: Props): JSX.Element => {
  const cats = categories || []
  const [selectedCat, setSelectedCat] = React.useState(null);
  const [selectedType, setSelectedType] = React.useState('all'); // 'all', 'courses', 'lite_bytes'
  const isLearner = userType === 'Learner';
  const isAdmin = !userType || userType === 'Admin';

  const categoryLabel = (category: Category): string => {
    if (I18n.locale === 'fr') {
      return category.name_fr || category.name;
    }

    return category.name;
  };

  const isInProgress = (course: Course): boolean => {
    if (!isLearner) return false;
    
    const completedCount = (course.lessons_completed ?? 0) as number;
    const submittedCount = (course.lessons_submitted ?? 0) as number;
    const progress = (course.progress ?? 0) as number;
    
    if (course.completed === true) return true;
    if (completedCount > 0) return true;
    if (submittedCount > 0) return true;
    if (progress > 0) return true;
    
    return false;
  };
  
  // Sort courses: in-progress first, then by required level
  const sortedCourses = React.useMemo(() => {
    if (!isLearner) return courses;
    
    return [...courses].sort((a, b) => {
      const aInProgress = isInProgress(a);
      const bInProgress = isInProgress(b);
      
      // In-progress courses come first
      if (aInProgress && !bInProgress) return -1;
      if (!aInProgress && bInProgress) return 1;
      
      // Otherwise sort by required level
      return a.required_level - b.required_level;
    });
  }, [courses, isLearner]);
  
  const [courseList, setCourseList] = React.useState(sortedCourses);

  const handleDelete = (id) => {
    if (confirm(I18n.t("views.forms.messaging.confirm_delete"))) {
      api
        .deleteCourse(id)
        .then((res) => {
          setCourseList(courseList.filter((item) => item.id !== id));
          window.location.href = res.url;
        })
        .catch((error) => console.error("Error", error));
    }
  };

  const filterByCategory = (coursesList: Course[]) => {
    if (selectedCat === null) {
      return coursesList;
    }
    return coursesList.filter(course => 
      course.categories.some(c => (typeof c === 'number' ? c === selectedCat : c?.id === selectedCat))
    );
  };

  const regularCourses = filterByCategory(sortedCourses.filter(course => !course.mini_lesson));
  
  const liteBytes = filterByCategory(sortedCourses.filter(course => course.mini_lesson === true));

  const renderCourseList = (coursesList: Course[], isMiniLesson) => {
    if (coursesList.length === 0) return null;

    return (
      <div className="courses_index__list">
        {coursesList.map((row, index) => (
          <ListItem
            title={row.title}
            requiredLevel={row.required_level}
            id={row.id}
            index={index}
            key={row.id}
            hideImage={false}
            isMiniLesson={isMiniLesson}
            image={(row as any).cover_image_url || row.file_url || null}
            handleDelete={handleDelete}
            isFacilitator={userType && (userType === 'Facilitator')}
            isLearner={userType && (userType === 'Learner')}
            learnerLevel={userLevel || 0}
            completed={row.completed}
            cardBackground="secondary"
            description={row.description || ""}
            showAdminControls={true}
            lessonsCompleted={row.lessons_completed}
            totalLessons={row.total_lessons}
            currentLessonTitle={row.current_lesson?.title}
            currentLessonNumber={row.current_lesson?.number}
            currentLessonId={row.current_lesson?.id}
            totalDuration={row.total_duration}
            level={row.difficulty_level}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="courses_index">
      {notice && <div className="header__top-bar alert alert-info">{notice}</div>}

      <Header
        title={I18n.t("models.course.attributes.courses")}
        titleButton={I18n.t("models.course.messaging.create")}
        locationButton="/admin/courses/new"
        buttonDisabled={
          userType && (userType === "Learner" || userType === "Facilitator")
        }
        showIcon={false}
      />
      
      {/* Filter by Category */}
      <div className="courses_index__section">
        <div className="header__top-bar">
          <h2 className="yellow">
              {I18n.t('models.course.type.filter_by_category')}
          </h2>
          {isAdmin && (
            <div>
              <a
                href="/admin/courses/edit_category"
                className="btn btn-course-pill me-2 mb-2"
              >
                {I18n.t('models.course.type.edit_category')}
              </a>
            </div>
          )}
        </div>
      <div
        className="cat-buttons"
      >
        <button
          className={`btn btn-square ${selectedCat === null ? 'active' : ''}`}
          key="all"
          onClick={() => { setSelectedCat(null) }}
        >
          {I18n.t('views.common.all')}
        </button>
        {cats.length > 0 ? (
          <>
            {cats.map((categories, index) => (
              <button
                className={`btn btn-square ${selectedCat === categories.id ? 'active' : ''}`}
                key={categories.id}
                onClick={() => { setSelectedCat(categories.id) }}
              >
                {categoryLabel(categories)}
              </button>
            ))}
          </>
        ) : null}
      </div>
      </div>

      {/* Filter by Type */}
      <div className="courses_index__section" style={{ marginTop: '1.25rem'}} >
        <h2 className="yellow">
          {I18n.t('models.course.type.filter_by_type')}
        </h2>
        <div
          className="type-buttons"
        >
          <button
            className={`btn btn-course-pill ${selectedType === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedType('all')}
          >
            {I18n.t('views.common.all') || 'All'}
          </button>
        <button
          className={`btn btn-course-pill ${selectedType === 'courses' ? 'active' : ''}`}
          onClick={() => setSelectedType('courses')}
        >
          {I18n.t('models.course.type.courses') || 'Courses'}
        </button>
        <button
          className={`btn btn-course-pill ${selectedType === 'lite_bytes' ? 'active' : ''}`}
          onClick={() => setSelectedType('lite_bytes')}
        >
          {I18n.t('models.course.type.lite_bytes') || 'Lite Bytes'}
        </button>
        </div>
      </div>

      {/* Regular Courses Section */}
      {selectedType !== 'lite_bytes' && regularCourses.length > 0 && (
        <div className="courses_index__section">
          <h2>
            {I18n.t('models.course.type.courses')}
          </h2>
          {renderCourseList(regularCourses, false)}
        </div>
      )}

      {/* Lite Bytes Section */}
      {selectedType !== 'courses' && liteBytes.length > 0 && (
        <div className="courses_index__section">
          <h3>
            {I18n.t('models.course.type.lite_bytes')}
          </h3>
          {renderCourseList(liteBytes, true)}
        </div>
      )}

      {/* Empty State */}
      {((selectedType === 'all' && regularCourses.length === 0 && liteBytes.length === 0) ||
        (selectedType === 'courses' && regularCourses.length === 0) ||
        (selectedType === 'lite_bytes' && liteBytes.length === 0)) && (
        <div className="courses_index__empty-state app-text">
          {I18n.t("models.course.messaging.empty_state")}
        </div>
      )}

    </div>
  );
};

export default IndexCourse;